<script setup>
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  nextTick,
  watch,
} from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import TasksAPI from 'dashboard/api/tasks';
import Avatar from 'next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import SelectMenu from 'dashboard/components-next/selectmenu/SelectMenu.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';

const props = defineProps({
  taskId: { type: [String, Number], default: null },
  // When rendered inside the list modal, navigation is handled by the parent
  // (emit) instead of routing to a new page.
  embedded: { type: Boolean, default: false },
  // Draft mode: render the same shell to compose a brand-new task.
  create: { type: Boolean, default: false },
});

const emit = defineEmits(['navigate', 'close', 'created']);

const store = useStore();
const router = useRouter();
const { t } = useI18n();
const { formatMessage } = useMessageFormatter();

const task = ref(null);
const comments = ref([]);
const newComment = ref('');
const isSaving = ref(false);
const error = ref('');

const commentEditorId = computed(() => `task-comment-${props.taskId}`);

// Inline title editing
const titleDraft = ref('');
const titleInput = ref(null);

// Inline description editing (rich text, same editor as comments)
const descriptionDraft = ref('');
const editingDescription = ref(false);
const descriptionEditorId = computed(() => `task-description-${props.taskId}`);

// Status dropdown
const statusOpen = ref(false);
const statusRef = ref(null);

// Assignee dropdown
const agents = computed(() => store.getters['agents/getAgents'] || []);
const assigneeOpen = ref(false);
const assigneeSearch = ref('');
const assigneeRef = ref(null);
const assigneeSearchInput = ref(null);

// Blocker dropdown
const blockerOpen = ref(false);
const blockerSearch = ref('');
const blockerRef = ref(null);
const blockerSearchInput = ref(null);
const blockerOptions = ref([]);

// Subtask creation
const newSubtask = ref('');
const subtaskFocused = ref(false);
const isCreatingSubtask = ref(false);
// Inline quick-add attributes for the subtask being composed
const newSubtaskAssigneeId = ref(null);
const newSubtaskPriority = ref(null);
const newSubtaskDueDate = ref(null);
const subAssigneeOpen = ref(false);
const subAssigneeRef = ref(null);
const subAssigneeSearch = ref('');
const subPriorityOpen = ref(false);
const subPriorityRef = ref(null);
const subDueOpen = ref(false);
const subDueRef = ref(null);

// Attachments
const fileInput = ref(null);
const isDraggingFile = ref(false);
const isUploadingAttachment = ref(false);

// Draft mode collects blockers/files locally and applies them after creation.
const draftBlockers = ref([]); // [{ display_id, title }]
const draftFiles = ref([]); // File[]

const STATUSES = ['open', 'in_progress', 'in_review', 'done', 'cancelled'];
const PRIORITIES = ['low', 'medium', 'high', 'urgent'];
const WAITING_OPTIONS = ['none', 'internal', 'client', 'vendor'];

// Solid status pills (ClickUp-style) on Radix `n-` scales → dark-mode safe.
// `dot` is the swatch color used in the dropdown list.
const STATUS_META = {
  open: { pill: 'bg-n-slate-9 text-white', dot: 'bg-n-slate-9' },
  in_progress: { pill: 'bg-n-blue-9 text-white', dot: 'bg-n-blue-9' },
  in_review: { pill: 'bg-n-amber-9 text-white', dot: 'bg-n-amber-9' },
  done: { pill: 'bg-n-teal-9 text-white', dot: 'bg-n-teal-9' },
  cancelled: { pill: 'bg-n-slate-8 text-white', dot: 'bg-n-slate-8' },
};
const PRIORITY_META = {
  urgent: 'text-n-ruby-9',
  high: 'text-n-amber-9',
  medium: 'text-n-blue-9',
  low: 'text-n-slate-9',
};

const loadComments = async () => {
  const r = await TasksAPI.getComments(props.taskId);
  comments.value = r.data.payload || [];
};

const load = async () => {
  const data = await store.dispatch('fetchTask', { id: props.taskId });
  task.value = { ...data };
  titleDraft.value = data.title || '';
  descriptionDraft.value = data.description || '';
  await loadComments();
};

const save = async patch => {
  isSaving.value = true;
  error.value = '';
  try {
    const data = await store.dispatch('updateTask', {
      id: props.taskId,
      ...patch,
    });
    task.value = { ...data };
    titleDraft.value = data.title || '';
    descriptionDraft.value = data.description || '';
    await loadComments(); // pick up activity entries
  } catch (e) {
    error.value = e.response?.data?.message || 'Update failed';
  } finally {
    isSaving.value = false;
  }
};

// In draft mode, field edits mutate the local task; otherwise they persist.
const updateField = patch => {
  if (props.create) {
    task.value = { ...task.value, ...patch };
  } else {
    save(patch);
  }
};

const commitTitle = () => {
  if (props.create) return; // draft title is persisted on create
  const next = titleDraft.value.trim();
  if (!next || next === task.value.title) {
    titleDraft.value = task.value.title || '';
    return;
  }
  save({ title: next });
};

const startEditDescription = () => {
  descriptionDraft.value = task.value.description || '';
  editingDescription.value = true;
};

const commitDescription = () => {
  editingDescription.value = false;
  if (descriptionDraft.value === (task.value.description || '')) return;
  updateField({ description: descriptionDraft.value });
};

const cancelDescription = () => {
  editingDescription.value = false;
  descriptionDraft.value = task.value.description || '';
};

const createDraft = async () => {
  const title = titleDraft.value.trim();
  if (!title || isSaving.value) return;
  isSaving.value = true;
  error.value = '';
  try {
    const payload = {
      title,
      status: task.value.status,
      priority: task.value.priority,
      waiting_on: task.value.waiting_on,
    };
    if (task.value.assignee_id) payload.assignee_id = task.value.assignee_id;
    if (task.value.due_date) payload.due_date = task.value.due_date;
    if (descriptionDraft.value.trim())
      payload.description = descriptionDraft.value;
    const data = await store.dispatch('createTask', payload);
    // Apply drafted blockers and attachments to the newly created task.
    await Promise.all(
      draftBlockers.value.map(b =>
        store.dispatch('addBlocker', {
          id: data.display_id,
          blockerDisplayId: b.display_id,
        })
      )
    );
    await Promise.all(
      draftFiles.value.map(file =>
        TasksAPI.uploadAttachment(data.display_id, file)
      )
    );
    emit('created', data.display_id);
  } catch (e) {
    error.value = e.response?.data?.message || 'Create failed';
  } finally {
    isSaving.value = false;
  }
};

const onTitleEnter = e => {
  if (props.create) createDraft();
  else e.target.blur();
};

const submitComment = async () => {
  if (!newComment.value.trim()) return;
  await TasksAPI.createComment(props.taskId, { content: newComment.value });
  newComment.value = '';
  await loadComments();
};

const subtasks = computed(() => task.value?.subtasks || []);

const resetSubtaskDraft = () => {
  newSubtask.value = '';
  newSubtaskAssigneeId.value = null;
  newSubtaskPriority.value = null;
  newSubtaskDueDate.value = null;
  subAssigneeOpen.value = false;
  subPriorityOpen.value = false;
  subDueOpen.value = false;
  subAssigneeSearch.value = '';
};

const createSubtask = async () => {
  const title = newSubtask.value.trim();
  if (!title || isCreatingSubtask.value) return;
  isCreatingSubtask.value = true;
  try {
    const payload = { title, parent_id: task.value.id };
    if (newSubtaskAssigneeId.value)
      payload.assignee_id = newSubtaskAssigneeId.value;
    if (newSubtaskPriority.value) payload.priority = newSubtaskPriority.value;
    if (newSubtaskDueDate.value) payload.due_date = newSubtaskDueDate.value;
    await TasksAPI.create(payload);
    resetSubtaskDraft();
    await load();
  } finally {
    isCreatingSubtask.value = false;
  }
};

const cancelSubtask = () => {
  resetSubtaskDraft();
  subtaskFocused.value = false;
};

// One inline quick-add popover open at a time.
const openSubPopover = which => {
  subAssigneeOpen.value = which === 'assignee' ? !subAssigneeOpen.value : false;
  subPriorityOpen.value = which === 'priority' ? !subPriorityOpen.value : false;
  subDueOpen.value = which === 'due' ? !subDueOpen.value : false;
};

const subAssigneeName = computed(
  () => agents.value.find(a => a.id === newSubtaskAssigneeId.value)?.name || ''
);

const subFilteredAgents = computed(() => {
  const q = subAssigneeSearch.value.toLowerCase();
  if (!q) return agents.value;
  return agents.value.filter(a => a.name.toLowerCase().includes(q));
});

const selectSubAssignee = id => {
  newSubtaskAssigneeId.value = id;
  subAssigneeOpen.value = false;
  subAssigneeSearch.value = '';
};

const selectSubPriority = priority => {
  newSubtaskPriority.value = priority;
  subPriorityOpen.value = false;
};

const toIsoDate = d => {
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
};

const setSubDue = key => {
  const d = new Date();
  if (key === 'tomorrow') d.setDate(d.getDate() + 1);
  if (key === 'next_week') d.setDate(d.getDate() + 7);
  newSubtaskDueDate.value = toIsoDate(d);
  subDueOpen.value = false;
};

const toggleSubtask = async subtask => {
  const status = subtask.status === 'done' ? 'open' : 'done';
  await TasksAPI.update(subtask.display_id, { status });
  await load();
};

// In embedded (modal) mode, navigate within the modal via emit; otherwise route.
const navigateToTask = displayId => {
  if (props.embedded) {
    emit('navigate', displayId);
  } else {
    router.push({ name: 'task_details', params: { taskId: displayId } });
  }
};

const openSubtask = subtask => navigateToTask(subtask.display_id);

const fileTypeOf = file => {
  const type = file.type || '';
  if (type.startsWith('image/')) return 'image';
  if (type.startsWith('video/')) return 'video';
  if (type.startsWith('audio/')) return 'audio';
  return 'file';
};

// Normalized list: drafted files (create) or persisted attachments (edit).
const attachmentList = computed(() => {
  if (props.create) {
    return draftFiles.value.map((file, index) => ({
      key: `draft-${index}`,
      draft: true,
      draftIndex: index,
      filename: file.name,
      file_size: file.size,
      file_type: fileTypeOf(file),
      file_url: null,
    }));
  }
  return (task.value?.attachments || []).map(a => ({
    ...a,
    key: a.id,
    draft: false,
  }));
});

const uploadFiles = async fileList => {
  const files = Array.from(fileList || []);
  if (!files.length) return;
  if (props.create) {
    draftFiles.value.push(...files);
    return;
  }
  isUploadingAttachment.value = true;
  try {
    await Promise.all(
      files.map(file => TasksAPI.uploadAttachment(task.value.display_id, file))
    );
    await load();
  } finally {
    isUploadingAttachment.value = false;
  }
};

const openFileDialog = () => fileInput.value?.click();

const onFilesSelected = e => {
  uploadFiles(e.target.files);
  e.target.value = '';
};

const onDropFiles = e => {
  isDraggingFile.value = false;
  uploadFiles(e.dataTransfer?.files);
};

const removeAttachmentItem = async item => {
  if (item.draft) {
    draftFiles.value.splice(item.draftIndex, 1);
    return;
  }
  await TasksAPI.removeAttachment(task.value.display_id, item.id);
  await load();
};

const formatFileSize = bytes => {
  if (bytes === null || bytes === undefined) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const attachmentIcon = type =>
  ({
    image: 'i-lucide-image',
    video: 'i-lucide-video',
    audio: 'i-lucide-music',
  })[type] || 'i-lucide-file';

// display_ids of blockers shown as chips (drafted in create, server in edit).
const blockerNumbers = computed(() =>
  props.create
    ? draftBlockers.value.map(b => b.display_id)
    : task.value?.blocking_task_numbers || []
);

const removeBlocker = async blockerDisplayId => {
  if (props.create) {
    draftBlockers.value = draftBlockers.value.filter(
      b => b.display_id !== blockerDisplayId
    );
    return;
  }
  await store.dispatch('removeBlocker', {
    id: props.taskId,
    blockerDisplayId,
  });
  await load();
};

const selectStatus = status => {
  statusOpen.value = false;
  if (status !== task.value.status) updateField({ status });
};

const markDone = () => {
  if (task.value.status !== 'done') updateField({ status: 'done' });
};

const currentAssigneeName = computed(() => {
  if (!task.value?.assignee_id) return t('TASKS.FIELDS.UNASSIGNED');
  return (
    agents.value.find(a => a.id === task.value.assignee_id)?.name ||
    task.value.assignee?.name ||
    t('TASKS.FIELDS.UNASSIGNED')
  );
});

const isAssigned = computed(() => Boolean(task.value?.assignee_id));

// Linked entities shown as a full-width row above the description.
// Currently the backend exposes a single polymorphic `taskable` (ticket);
// contacts and projects can be appended here as those relations land.
const links = computed(() => {
  const out = [];
  const tk = task.value;
  if (!tk) return out;
  if (tk.taskable) {
    out.push({
      key: `ticket-${tk.taskable.display_id}`,
      icon: 'i-lucide-ticket',
      ref: `#${tk.taskable.display_id}`,
      label: tk.taskable.title,
      to: {
        name: 'ticket_details',
        params: { ticketId: tk.taskable.display_id },
      },
    });
  }
  return out;
});

const filteredAgents = computed(() => {
  const q = assigneeSearch.value.toLowerCase();
  const all = [
    { id: null, name: t('TASKS.FIELDS.UNASSIGNED') },
    ...agents.value,
  ];
  if (!q) return all;
  return all.filter(a => a.name.toLowerCase().includes(q));
});

const selectAssignee = async agentId => {
  assigneeOpen.value = false;
  assigneeSearch.value = '';
  if (props.create) {
    const agent = agents.value.find(a => a.id === agentId);
    task.value = {
      ...task.value,
      assignee_id: agentId,
      assignee: agent ? { name: agent.name } : null,
    };
    return;
  }
  const data = await store.dispatch('assignTask', {
    id: props.taskId,
    assigneeId: agentId,
  });
  task.value = { ...data };
  await loadComments();
};

const fetchBlockerOptions = async () => {
  if (!task.value) return;
  try {
    // No self to exclude while drafting a new task.
    const response = await TasksAPI.getAvailableBlockers(task.value.id);
    blockerOptions.value = response.data.payload || [];
  } catch (e) {
    blockerOptions.value = [];
  }
};

const filteredBlockerOptions = computed(() => {
  const existing = new Set(blockerNumbers.value.map(String));
  const q = blockerSearch.value.toLowerCase().replace(/^tsk-/, '');
  return blockerOptions.value.filter(opt => {
    if (existing.has(String(opt.display_id))) return false;
    if (!q) return true;
    return (
      String(opt.display_id).includes(q) ||
      opt.title?.toLowerCase().includes(q.toLowerCase())
    );
  });
});

const selectBlocker = async option => {
  blockerSearch.value = '';
  if (props.create) {
    if (!draftBlockers.value.some(b => b.display_id === option.display_id)) {
      draftBlockers.value.push({
        display_id: option.display_id,
        title: option.title,
      });
    }
    await nextTick();
    blockerSearchInput.value?.focus();
    return;
  }
  await store.dispatch('addBlocker', {
    id: props.taskId,
    blockerDisplayId: option.display_id,
  });
  await load();
  await fetchBlockerOptions();
  await nextTick();
  blockerSearchInput.value?.focus();
};

const closeDropdowns = e => {
  if (statusRef.value && !statusRef.value.contains(e.target)) {
    statusOpen.value = false;
  }
  if (assigneeRef.value && !assigneeRef.value.contains(e.target)) {
    assigneeOpen.value = false;
    assigneeSearch.value = '';
  }
  if (blockerRef.value && !blockerRef.value.contains(e.target)) {
    blockerOpen.value = false;
    blockerSearch.value = '';
  }
  if (subAssigneeRef.value && !subAssigneeRef.value.contains(e.target)) {
    subAssigneeOpen.value = false;
    subAssigneeSearch.value = '';
  }
  if (subPriorityRef.value && !subPriorityRef.value.contains(e.target)) {
    subPriorityOpen.value = false;
  }
  if (subDueRef.value && !subDueRef.value.contains(e.target)) {
    subDueOpen.value = false;
  }
};

watch(assigneeOpen, async val => {
  if (val) {
    await store.dispatch('agents/get');
    await nextTick();
    assigneeSearchInput.value?.focus();
  }
});

watch(blockerOpen, async val => {
  if (val) {
    await fetchBlockerOptions();
    await nextTick();
    blockerSearchInput.value?.focus();
  }
});

// Walk up the tree: a subtask goes back to its parent task; a top-level task
// goes back to the list (or closes the modal when embedded).
const goBack = () => {
  if (task.value?.parent) {
    navigateToTask(task.value.parent.display_id);
  } else if (props.embedded) {
    emit('close');
  } else {
    router.push({ name: 'tasks_list' });
  }
};
const formatDate = d => (d ? new Date(d).toLocaleString() : '—');
const formatDay = d =>
  d
    ? new Date(d).toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'short',
      })
    : '';
const statusLabel = s => t(`TASKS.STATUS.${s.toUpperCase()}`);
const priorityLabel = p => t(`TASKS.PRIORITY.${p.toUpperCase()}`);
const waitingLabel = w => t(`TASKS.WAITING_ON.${w.toUpperCase()}`);

const priorityOptions = computed(() =>
  PRIORITIES.map(p => ({ value: p, label: priorityLabel(p) }))
);
const waitingOptions = computed(() =>
  WAITING_OPTIONS.map(w => ({ value: w, label: waitingLabel(w) }))
);

const statusMeta = computed(
  () => STATUS_META[task.value?.status] || STATUS_META.open
);

onMounted(() => {
  document.addEventListener('click', closeDropdowns);
  store.dispatch('agents/get');
  if (props.create) {
    task.value = {
      title: '',
      status: 'open',
      priority: 'medium',
      waiting_on: 'none',
      assignee_id: null,
    };
    titleDraft.value = '';
    nextTick(() => titleInput.value?.focus());
  } else {
    load();
  }
});

onBeforeUnmount(() => {
  document.removeEventListener('click', closeDropdowns);
});

watch(() => props.taskId, load);
</script>

<template>
  <div
    v-if="task"
    class="flex flex-col bg-n-background lg:flex-row"
    :class="create ? '' : 'h-full'"
  >
    <!-- ============ LEFT: main content panel ============ -->
    <main class="flex flex-col flex-1 min-w-0 overflow-y-auto">
      <!-- Breadcrumb / context bar -->
      <div class="flex items-center justify-between gap-2 px-6 pt-4 pb-1">
        <div class="flex items-center gap-2 min-w-0 text-sm text-n-slate-11">
          <Button
            icon="i-lucide-arrow-left"
            variant="ghost"
            color="slate"
            size="sm"
            @click="goBack"
          />
          <span class="flex items-center gap-1.5">
            <span class="i-lucide-circle-dot text-n-slate-10" />
            {{ t('TASKS.HEADER_TITLE') }}
          </span>
          <span class="text-n-slate-8">/</span>
          <template v-if="task.parent">
            <button
              type="button"
              class="truncate max-w-[12rem] hover:text-n-blue-text"
              @click="navigateToTask(task.parent.display_id)"
            >
              {{ task.parent.title }}
            </button>
            <span class="text-n-slate-8">/</span>
          </template>
          <span class="font-mono text-n-slate-10">
            <template v-if="create">{{ t('TASKS.ACTIONS.NEW') }}</template>
            <template v-else>
              {{ t('TASKS.NUMBER_PREFIX') }}{{ task.display_id }}
            </template>
          </span>
          <span
            v-if="isSaving"
            class="flex items-center gap-1 text-xs text-n-slate-10"
          >
            <span class="i-lucide-loader-circle animate-spin" />
            {{ t('TASKS.ACTIONS.SAVING') }}
          </span>
        </div>
      </div>

      <div class="flex flex-col w-full max-w-3xl gap-6 px-6 pt-2 pb-10">
        <!-- Title -->
        <input
          ref="titleInput"
          v-model="titleDraft"
          class="w-full text-2xl font-bold leading-snug bg-transparent rounded-md text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:bg-n-alpha-1 focus:px-2 focus:-mx-2 focus:py-1"
          :placeholder="t('TASKS.FIELDS.TITLE')"
          @keyup.enter="onTitleEnter"
          @blur="commitTitle"
        />

        <p v-if="error" class="text-sm text-n-ruby-11">{{ error }}</p>

        <!-- Metadata grid (two columns) -->
        <section class="grid grid-cols-1 gap-x-10 gap-y-3.5 sm:grid-cols-2">
          <!-- Status -->
          <div class="flex items-start gap-3 min-w-0">
            <span
              class="flex items-center gap-2 mt-1.5 text-sm w-28 shrink-0 text-n-slate-11"
            >
              <span class="i-lucide-loader text-n-slate-10" />
              {{ t('TASKS.FIELDS.STATUS') }}
            </span>
            <div ref="statusRef" class="relative flex items-center gap-1.5">
              <button
                type="button"
                class="inline-flex items-center gap-1.5 px-2.5 h-7 text-xs font-semibold tracking-wide uppercase rounded-md transition-all hover:brightness-110"
                :class="statusMeta.pill"
                @click.stop="statusOpen = !statusOpen"
              >
                <span class="i-lucide-circle-check opacity-90" />
                {{ statusLabel(task.status) }}
                <span class="i-lucide-chevron-down opacity-70" />
              </button>
              <button
                v-if="task.status !== 'done'"
                v-tooltip.top="statusLabel('done')"
                type="button"
                class="flex items-center justify-center text-n-slate-10 h-7 w-7 rounded-md hover:bg-n-alpha-1 hover:text-n-teal-10"
                @click="markDone"
              >
                <span class="i-lucide-circle-check" />
              </button>
              <ul
                v-if="statusOpen"
                class="absolute left-0 z-50 flex flex-col gap-0.5 p-1 mt-1 border rounded-lg shadow-lg top-full bg-n-solid-2 border-n-weak w-44"
              >
                <li
                  v-for="s in STATUSES"
                  :key="s"
                  class="flex items-center gap-2 px-2.5 py-1.5 text-sm rounded-md cursor-pointer hover:bg-n-alpha-1"
                  :class="
                    s === task.status
                      ? 'text-n-slate-12 font-medium'
                      : 'text-n-slate-11'
                  "
                  @click="selectStatus(s)"
                >
                  <span
                    class="w-2 h-2 rounded-full"
                    :class="STATUS_META[s].dot"
                  />
                  {{ statusLabel(s) }}
                  <span
                    v-if="s === task.status"
                    class="ml-auto i-lucide-check text-n-blue-text"
                  />
                </li>
              </ul>
            </div>
          </div>

          <!-- Assignee -->
          <div class="flex items-start gap-3 min-w-0">
            <span
              class="flex items-center gap-2 mt-1.5 text-sm w-28 shrink-0 text-n-slate-11"
            >
              <span class="i-lucide-user text-n-slate-10" />
              {{ t('TASKS.FIELDS.ASSIGNEE') }}
            </span>
            <div ref="assigneeRef" class="relative flex-1 min-w-0">
              <button
                v-if="!assigneeOpen"
                type="button"
                class="flex items-center justify-between w-full gap-2 px-2 text-sm text-left rounded-md h-7 hover:bg-n-alpha-1"
                :class="isAssigned ? 'text-n-slate-12' : 'text-n-slate-10'"
                @click.stop="assigneeOpen = true"
              >
                <span class="flex items-center gap-2 truncate">
                  <Avatar
                    v-if="isAssigned"
                    :name="currentAssigneeName"
                    :size="20"
                  />
                  <span class="truncate">{{ currentAssigneeName }}</span>
                </span>
                <span class="i-lucide-chevron-down text-n-slate-10 shrink-0" />
              </button>
              <div
                v-else
                class="flex items-center px-2 rounded-md h-7 outline-1 outline outline-n-brand bg-n-alpha-black2"
              >
                <input
                  ref="assigneeSearchInput"
                  v-model="assigneeSearch"
                  type="text"
                  :placeholder="t('TASKS.FIELDS.ASSIGNEE_PLACEHOLDER')"
                  class="w-full text-sm bg-transparent border-none reset-base text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none"
                  @click.stop
                />
              </div>
              <ul
                v-if="assigneeOpen"
                class="absolute left-0 right-0 z-50 py-1 mt-1 overflow-y-auto border rounded-lg shadow-lg bg-n-solid-2 border-n-weak max-h-44"
              >
                <li
                  v-for="agent in filteredAgents"
                  :key="agent.id ?? 'none'"
                  class="flex items-center gap-2 px-2.5 py-1.5 text-sm cursor-pointer hover:bg-n-alpha-1"
                  :class="
                    (agent.id ?? null) === (task.assignee_id ?? null)
                      ? 'text-n-blue-text font-medium'
                      : 'text-n-slate-12'
                  "
                  @mousedown.prevent="selectAssignee(agent.id)"
                >
                  <Avatar :name="agent.name" :size="20" />
                  <span class="truncate">{{ agent.name }}</span>
                </li>
                <li
                  v-if="filteredAgents.length === 0"
                  class="px-3 py-2 text-xs text-center text-n-slate-10"
                >
                  {{ t('TASKS.FIELDS.ASSIGNEE_PLACEHOLDER') }}
                </li>
              </ul>
            </div>
          </div>

          <!-- Priority -->
          <div class="flex items-start gap-3 min-w-0">
            <span
              class="flex items-center gap-2 mt-1.5 text-sm w-28 shrink-0 text-n-slate-11"
            >
              <span
                class="i-lucide-flag"
                :class="PRIORITY_META[task.priority]"
              />
              {{ t('TASKS.FIELDS.PRIORITY') }}
            </span>
            <div class="flex flex-1 min-w-0">
              <SelectMenu
                :model-value="task.priority"
                :options="priorityOptions"
                :label="priorityLabel(task.priority)"
                sub-menu-position="bottom"
                @update:model-value="updateField({ priority: $event })"
              />
            </div>
          </div>

          <!-- Waiting on -->
          <div class="flex items-start gap-3 min-w-0">
            <span
              class="flex items-center gap-2 mt-1.5 text-sm w-28 shrink-0 text-n-slate-11"
            >
              <span class="i-lucide-hourglass text-n-slate-10" />
              {{ t('TASKS.WAITING_ON.LABEL') }}
            </span>
            <div class="flex flex-1 min-w-0">
              <SelectMenu
                :model-value="task.waiting_on || 'none'"
                :options="waitingOptions"
                :label="waitingLabel(task.waiting_on || 'none')"
                sub-menu-position="bottom"
                @update:model-value="updateField({ waiting_on: $event })"
              />
            </div>
          </div>

          <!-- Due date -->
          <div class="flex items-start gap-3 min-w-0">
            <span
              class="flex items-center gap-2 mt-1.5 text-sm w-28 shrink-0 text-n-slate-11"
            >
              <span class="i-lucide-calendar text-n-slate-10" />
              {{ t('TASKS.FIELDS.DUE_DATE') }}
            </span>
            <div class="flex-1 min-w-0 max-w-44">
              <Input
                type="date"
                size="sm"
                :model-value="task.due_date"
                @update:model-value="updateField({ due_date: $event })"
              />
            </div>
          </div>

          <!-- Relations / Blocked by -->
          <div class="flex items-start gap-3 min-w-0">
            <span
              class="flex items-center gap-2 mt-1.5 text-sm w-28 shrink-0 text-n-slate-11"
            >
              <span class="i-lucide-git-fork text-n-slate-10" />
              {{ t('TASKS.FIELDS.BLOCKED_BY') }}
            </span>
            <div class="flex flex-col flex-1 min-w-0 gap-1.5">
              <ul v-if="blockerNumbers.length" class="flex flex-wrap gap-1.5">
                <li
                  v-for="num in blockerNumbers"
                  :key="num"
                  class="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium border rounded-full bg-n-amber-3 text-n-amber-11 border-n-amber-6"
                >
                  {{ t('TASKS.NUMBER_PREFIX') }}{{ num }}
                  <button
                    class="i-lucide-x text-n-amber-11 hover:text-n-amber-12"
                    @click="removeBlocker(num)"
                  />
                </li>
              </ul>
              <div ref="blockerRef" class="relative">
                <button
                  v-if="!blockerOpen"
                  type="button"
                  class="inline-flex items-center gap-1.5 px-2 text-xs text-left rounded-md h-7 w-fit text-n-slate-10 hover:bg-n-alpha-1 hover:text-n-slate-11"
                  @click.stop="blockerOpen = true"
                >
                  <span class="i-lucide-plus" />
                  {{ t('TASKS.ACTIONS.ADD_BLOCKER') }}
                </button>
                <div
                  v-else
                  class="flex items-center px-3 rounded-lg h-9 outline-1 outline outline-n-brand bg-n-alpha-black2"
                >
                  <input
                    ref="blockerSearchInput"
                    v-model="blockerSearch"
                    type="text"
                    :placeholder="t('TASKS.FIELDS.BLOCKED_BY_PLACEHOLDER')"
                    class="w-full text-sm bg-transparent border-none reset-base text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none"
                    @click.stop
                  />
                </div>
                <ul
                  v-if="blockerOpen"
                  class="absolute left-0 z-50 w-64 py-1 mt-1 overflow-y-auto border rounded-lg shadow-lg bg-n-solid-2 border-n-weak max-h-60"
                >
                  <li
                    v-for="option in filteredBlockerOptions"
                    :key="option.id"
                    class="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer text-n-slate-12 hover:bg-n-alpha-1"
                    @mousedown.prevent="selectBlocker(option)"
                  >
                    <span class="font-mono text-xs text-n-slate-10">
                      {{ t('TASKS.NUMBER_PREFIX') }}{{ option.display_id }}
                    </span>
                    <span class="truncate">{{ option.title }}</span>
                  </li>
                  <li
                    v-if="filteredBlockerOptions.length === 0"
                    class="px-3 py-2 text-xs text-center text-n-slate-10"
                  >
                    {{ t('TASKS.FIELDS.BLOCKED_BY_EMPTY') }}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <!-- Relationships: ticket / contact / (projects next) -->
        <section
          v-if="links.length"
          class="flex flex-wrap items-center gap-2 py-2 border-y border-n-weak"
        >
          <span
            class="flex items-center gap-1.5 mr-1 text-xs font-semibold tracking-wide uppercase text-n-slate-10"
          >
            <span class="i-lucide-link" />
            {{ t('TASKS.FIELDS.RELATIONSHIPS') }}
          </span>
          <router-link
            v-for="link in links"
            :key="link.key"
            :to="link.to"
            class="inline-flex items-center min-w-0 gap-1.5 px-2.5 h-7 text-xs font-medium border rounded-full bg-n-slate-3 text-n-slate-11 border-n-slate-6 hover:text-n-blue-text"
          >
            <span :class="link.icon" class="shrink-0" />
            <span class="shrink-0">{{ link.ref }}</span>
            <span v-if="link.label" class="truncate text-n-slate-10">
              {{ link.label }}
            </span>
          </router-link>
        </section>

        <!-- Description -->
        <section class="flex flex-col gap-2">
          <h3
            class="flex items-center gap-1.5 text-xs font-semibold tracking-wide uppercase text-n-slate-10"
          >
            <span class="i-lucide-align-left" />
            {{ t('TASKS.FIELDS.DESCRIPTION') }}
          </h3>

          <!-- Create mode: always-on editor (saved with the task) -->
          <div
            v-if="create"
            class="p-2 border rounded-xl border-n-weak bg-n-solid-2"
          >
            <WootMessageEditor
              v-model="descriptionDraft"
              :editor-id="descriptionEditorId"
              :placeholder="t('TASKS.FIELDS.DESCRIPTION_PLACEHOLDER')"
              is-private
              :enable-canned-responses="false"
              :enable-variables="false"
              class="task-description-editor min-h-[80px] [&_.ProseMirror]:max-h-[300px] [&_.ProseMirror]:overflow-y-auto"
            />
          </div>

          <!-- Edit mode (existing task): rich text editor + save/cancel -->
          <div v-else-if="editingDescription" class="flex flex-col gap-2">
            <div class="p-2 border rounded-xl border-n-weak bg-n-solid-2">
              <WootMessageEditor
                v-model="descriptionDraft"
                :editor-id="descriptionEditorId"
                :placeholder="t('TASKS.FIELDS.DESCRIPTION_PLACEHOLDER')"
                is-private
                :enable-canned-responses="false"
                :enable-variables="false"
                class="task-description-editor min-h-[80px] [&_.ProseMirror]:max-h-[300px] [&_.ProseMirror]:overflow-y-auto"
              />
            </div>
            <div class="flex justify-end gap-2">
              <Button
                :label="t('TASKS.SUBTASKS.CANCEL')"
                variant="ghost"
                color="slate"
                size="sm"
                @click="cancelDescription"
              />
              <Button
                :label="t('TASKS.ACTIONS.SAVE')"
                size="sm"
                color="blue"
                @click="commitDescription"
              />
            </div>
          </div>

          <!-- Read mode: rendered HTML, click to edit -->
          <div
            v-else
            class="p-4 text-sm leading-relaxed transition-colors border cursor-text rounded-xl border-n-weak bg-n-solid-2 min-h-[80px] hover:border-n-slate-6"
            @click="startEditDescription"
          >
            <div
              v-if="task.description"
              v-dompurify-html="formatMessage(task.description, false, false)"
              class="prose prose-sm max-w-none text-n-slate-12 [&_.prosemirror-mention-node]:font-semibold"
            />
            <span v-else class="text-n-slate-10">
              {{ t('TASKS.DESCRIPTION_EMPTY') }}
            </span>
          </div>
        </section>

        <!-- Subtasks -->
        <section v-if="!create" class="flex flex-col gap-2">
          <h3
            class="flex items-center gap-1.5 text-xs font-semibold tracking-wide uppercase text-n-slate-10"
          >
            <span class="i-lucide-list-tree" />
            {{ t('TASKS.SUBTASKS.TITLE') }}
            <span v-if="subtasks.length" class="text-n-slate-9">
              {{ subtasks.length }}
            </span>
          </h3>

          <!-- Subtasks + inline create share one container -->
          <div
            class="flex flex-col border divide-y rounded-xl divide-n-weak border-n-weak bg-n-solid-2"
          >
            <div
              v-for="st in subtasks"
              :key="st.id"
              class="flex items-center gap-2.5 px-3 py-2 group hover:bg-n-alpha-1"
            >
              <button
                type="button"
                class="flex items-center justify-center w-4 h-4 border-2 rounded-full shrink-0 transition-colors"
                :class="
                  st.status === 'done'
                    ? 'bg-n-teal-9 border-n-teal-9 text-white'
                    : 'border-n-slate-7 hover:border-n-teal-9 text-transparent'
                "
                @click.stop="toggleSubtask(st)"
              >
                <span class="i-lucide-check text-[10px]" />
              </button>
              <button
                type="button"
                class="flex-1 min-w-0 text-sm text-left truncate"
                :class="
                  st.status === 'done'
                    ? 'text-n-slate-10 line-through'
                    : 'text-n-slate-12'
                "
                @click="openSubtask(st)"
              >
                <span class="mr-1.5 font-mono text-xs text-n-slate-9">
                  {{ t('TASKS.NUMBER_PREFIX') }}{{ st.display_id }}
                </span>
                {{ st.title }}
              </button>
              <span
                v-if="st.priority && st.priority !== 'medium'"
                class="i-lucide-flag shrink-0"
                :class="PRIORITY_META[st.priority]"
              />
              <Avatar
                v-if="st.assignee"
                :name="st.assignee.name"
                :size="20"
                class="shrink-0"
              />
              <span v-if="st.due_date" class="text-xs shrink-0 text-n-slate-10">
                {{ formatDay(st.due_date) }}
              </span>
            </div>

            <!-- Inline create row (last row in the container) -->
            <div
              class="flex items-center gap-2.5 px-3 h-10 focus-within:bg-n-alpha-1"
            >
              <span
                class="w-4 h-4 border-2 border-dashed rounded-full shrink-0 border-n-slate-7"
              />
              <input
                v-model="newSubtask"
                type="text"
                :placeholder="t('TASKS.SUBTASKS.PLACEHOLDER')"
                class="flex-1 min-w-0 text-sm bg-transparent border-none reset-base text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none"
                @focus="subtaskFocused = true"
                @keyup.enter="createSubtask"
                @keyup.esc="cancelSubtask"
              />

              <!-- Quick-add toolbar (assignee / priority / due date) -->
              <div class="flex items-center gap-0.5 shrink-0">
                <!-- Assignee -->
                <div ref="subAssigneeRef" class="relative">
                  <button
                    v-tooltip.top="t('TASKS.FIELDS.ASSIGNEE')"
                    type="button"
                    class="flex items-center justify-center rounded-md w-7 h-7 hover:bg-n-alpha-2"
                    @click.stop="openSubPopover('assignee')"
                  >
                    <Avatar
                      v-if="newSubtaskAssigneeId"
                      :name="subAssigneeName"
                      :size="20"
                    />
                    <span v-else class="i-lucide-user-plus text-n-slate-10" />
                  </button>
                  <div
                    v-if="subAssigneeOpen"
                    class="absolute z-50 p-1 mt-1 border rounded-lg shadow-lg top-full ltr:right-0 rtl:left-0 w-56 bg-n-solid-2 border-n-weak"
                  >
                    <input
                      v-model="subAssigneeSearch"
                      type="text"
                      :placeholder="t('TASKS.FIELDS.ASSIGNEE_PLACEHOLDER')"
                      class="w-full px-2 py-1 text-sm border-none rounded-md reset-base bg-n-alpha-black2 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none"
                      @click.stop
                    />
                    <ul class="mt-1 overflow-y-auto max-h-44">
                      <li
                        class="flex items-center gap-2 px-2 py-1.5 text-sm rounded-md cursor-pointer text-n-slate-11 hover:bg-n-alpha-1"
                        @mousedown.prevent="selectSubAssignee(null)"
                      >
                        {{ t('TASKS.FIELDS.UNASSIGNED') }}
                      </li>
                      <li
                        v-for="a in subFilteredAgents"
                        :key="a.id"
                        class="flex items-center gap-2 px-2 py-1.5 text-sm rounded-md cursor-pointer text-n-slate-12 hover:bg-n-alpha-1"
                        @mousedown.prevent="selectSubAssignee(a.id)"
                      >
                        <Avatar :name="a.name" :size="20" />
                        <span class="truncate">{{ a.name }}</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <!-- Priority -->
                <div ref="subPriorityRef" class="relative">
                  <button
                    v-tooltip.top="t('TASKS.FIELDS.PRIORITY')"
                    type="button"
                    class="flex items-center justify-center rounded-md w-7 h-7 hover:bg-n-alpha-2"
                    @click.stop="openSubPopover('priority')"
                  >
                    <span
                      class="i-lucide-flag"
                      :class="
                        newSubtaskPriority
                          ? PRIORITY_META[newSubtaskPriority]
                          : 'text-n-slate-10'
                      "
                    />
                  </button>
                  <div
                    v-if="subPriorityOpen"
                    class="absolute z-50 flex flex-col p-1 mt-1 border rounded-lg shadow-lg top-full ltr:right-0 rtl:left-0 w-44 bg-n-solid-2 border-n-weak"
                  >
                    <button
                      v-for="p in PRIORITIES"
                      :key="p"
                      type="button"
                      class="flex items-center gap-2 px-2 py-1.5 text-sm text-left rounded-md hover:bg-n-alpha-1"
                      :class="
                        newSubtaskPriority === p
                          ? 'text-n-slate-12 font-medium'
                          : 'text-n-slate-11'
                      "
                      @click="selectSubPriority(p)"
                    >
                      <span class="i-lucide-flag" :class="PRIORITY_META[p]" />
                      {{ priorityLabel(p) }}
                      <span
                        v-if="newSubtaskPriority === p"
                        class="ml-auto i-lucide-check text-n-blue-text"
                      />
                    </button>
                    <button
                      type="button"
                      class="flex items-center gap-2 px-2 py-1.5 mt-0.5 text-sm text-left border-t rounded-md border-n-weak text-n-slate-10 hover:bg-n-alpha-1"
                      @click="selectSubPriority(null)"
                    >
                      <span class="i-lucide-x" />
                      {{ t('TASKS.SUBTASKS.CLEAR_PRIORITY') }}
                    </button>
                  </div>
                </div>

                <!-- Due date -->
                <div ref="subDueRef" class="relative">
                  <button
                    v-tooltip.top="t('TASKS.FIELDS.DUE_DATE')"
                    type="button"
                    class="flex items-center justify-center h-7 rounded-md hover:bg-n-alpha-2"
                    :class="newSubtaskDueDate ? 'px-1' : 'w-7'"
                    @click.stop="openSubPopover('due')"
                  >
                    <span
                      v-if="newSubtaskDueDate"
                      class="px-1.5 py-0.5 text-xs font-medium rounded bg-n-teal-3 text-n-teal-11"
                    >
                      {{ formatDay(newSubtaskDueDate) }}
                    </span>
                    <span
                      v-else
                      class="i-lucide-calendar-plus text-n-slate-10"
                    />
                  </button>
                  <div
                    v-if="subDueOpen"
                    class="absolute z-50 flex flex-col gap-1 p-2 mt-1 border rounded-lg shadow-lg top-full ltr:right-0 rtl:left-0 w-52 bg-n-solid-2 border-n-weak"
                  >
                    <button
                      type="button"
                      class="px-2 py-1.5 text-sm text-left rounded-md text-n-slate-12 hover:bg-n-alpha-1"
                      @click="setSubDue('today')"
                    >
                      {{ t('TASKS.SUBTASKS.DUE_TODAY') }}
                    </button>
                    <button
                      type="button"
                      class="px-2 py-1.5 text-sm text-left rounded-md text-n-slate-12 hover:bg-n-alpha-1"
                      @click="setSubDue('tomorrow')"
                    >
                      {{ t('TASKS.SUBTASKS.DUE_TOMORROW') }}
                    </button>
                    <button
                      type="button"
                      class="px-2 py-1.5 text-sm text-left rounded-md text-n-slate-12 hover:bg-n-alpha-1"
                      @click="setSubDue('next_week')"
                    >
                      {{ t('TASKS.SUBTASKS.DUE_NEXT_WEEK') }}
                    </button>
                    <input
                      v-model="newSubtaskDueDate"
                      type="date"
                      class="w-full px-2 py-1.5 mt-1 text-sm border rounded-md reset-base border-n-weak bg-n-alpha-black2 text-n-slate-12 focus:outline-none focus:border-n-brand"
                      @click.stop
                    />
                    <button
                      v-if="newSubtaskDueDate"
                      type="button"
                      class="px-2 py-1 text-xs text-left rounded-md text-n-slate-10 hover:bg-n-alpha-1"
                      @click="newSubtaskDueDate = null"
                    >
                      {{ t('TASKS.SUBTASKS.DUE_CLEAR') }}
                    </button>
                  </div>
                </div>
              </div>

              <template v-if="subtaskFocused || newSubtask">
                <Button
                  :label="t('TASKS.SUBTASKS.CANCEL')"
                  variant="ghost"
                  color="slate"
                  size="xs"
                  @click="cancelSubtask"
                />
                <Button
                  size="xs"
                  color="blue"
                  trailing-icon
                  icon="i-lucide-corner-down-left"
                  :label="t('TASKS.SUBTASKS.SAVE')"
                  :is-loading="isCreatingSubtask"
                  :disabled="!newSubtask.trim() || isCreatingSubtask"
                  @click="createSubtask"
                />
              </template>
            </div>
          </div>
        </section>

        <!-- Attachments -->
        <section class="flex flex-col gap-2">
          <h3
            class="flex items-center justify-between text-xs font-semibold tracking-wide uppercase text-n-slate-10"
          >
            <span class="flex items-center gap-1.5">
              <span class="i-lucide-paperclip" />
              {{ t('TASKS.ATTACHMENTS.TITLE') }}
              <span v-if="attachmentList.length" class="text-n-slate-9">
                {{ attachmentList.length }}
              </span>
            </span>
            <button
              type="button"
              class="flex items-center justify-center rounded-md w-6 h-6 text-n-slate-10 hover:bg-n-alpha-1 hover:text-n-slate-12"
              @click="openFileDialog"
            >
              <span class="i-lucide-plus" />
            </button>
          </h3>

          <!-- Files (drafted or uploaded) -->
          <ul v-if="attachmentList.length" class="flex flex-col gap-2">
            <li
              v-for="att in attachmentList"
              :key="att.key"
              class="flex items-center gap-2.5 px-3 py-2 border rounded-lg group border-n-weak bg-n-solid-2"
            >
              <span
                :class="attachmentIcon(att.file_type)"
                class="text-n-slate-10 shrink-0"
              />
              <a
                v-if="att.file_url"
                :href="att.file_url"
                target="_blank"
                rel="noopener noreferrer"
                class="flex-1 min-w-0 text-sm truncate text-n-slate-12 hover:text-n-blue-text"
              >
                {{ att.filename }}
              </a>
              <span
                v-else
                class="flex-1 min-w-0 text-sm truncate text-n-slate-12"
              >
                {{ att.filename }}
              </span>
              <span class="text-xs shrink-0 text-n-slate-10">
                {{ formatFileSize(att.file_size) }}
              </span>
              <button
                v-tooltip.top="t('TASKS.ATTACHMENTS.REMOVE')"
                type="button"
                class="flex items-center justify-center w-6 h-6 rounded-md opacity-0 group-hover:opacity-100 text-n-slate-10 hover:text-n-ruby-9 hover:bg-n-alpha-1"
                @click="removeAttachmentItem(att)"
              >
                <span class="i-lucide-x" />
              </button>
            </li>
          </ul>

          <!-- Drag & drop zone -->
          <button
            type="button"
            class="flex flex-col items-center justify-center gap-1 px-4 py-6 text-sm text-center transition-colors border-2 border-dashed rounded-xl"
            :class="
              isDraggingFile
                ? 'border-n-brand bg-n-alpha-1 text-n-slate-12'
                : 'border-n-weak text-n-slate-10 hover:border-n-slate-6'
            "
            @click="openFileDialog"
            @dragover.prevent="isDraggingFile = true"
            @dragenter.prevent="isDraggingFile = true"
            @dragleave.prevent="isDraggingFile = false"
            @drop.prevent="onDropFiles"
          >
            <span
              v-if="isUploadingAttachment"
              class="flex items-center gap-2 i-lucide-loader-circle animate-spin"
            />
            <template v-else>
              <span class="text-lg i-lucide-cloud-upload" />
              <span>
                {{ t('TASKS.ATTACHMENTS.DROP_HINT') }}
                <span class="underline text-n-blue-text">
                  {{ t('TASKS.ATTACHMENTS.UPLOAD') }}
                </span>
              </span>
            </template>
          </button>

          <input
            ref="fileInput"
            type="file"
            multiple
            class="hidden"
            @change="onFilesSelected"
          />
        </section>

        <!-- Create footer (draft mode) -->
        <div
          v-if="create"
          class="flex justify-end gap-2 pt-4 mt-2 border-t border-n-weak"
        >
          <Button
            :label="t('TASKS.SUBTASKS.CANCEL')"
            variant="ghost"
            color="slate"
            size="sm"
            @click="emit('close')"
          />
          <Button
            :label="t('TASKS.ACTIONS.CREATE')"
            icon="i-lucide-plus"
            size="sm"
            color="blue"
            :is-loading="isSaving"
            :disabled="!titleDraft.trim() || isSaving"
            @click="createDraft"
          />
        </div>
      </div>
    </main>

    <!-- ============ RIGHT: activity & comments panel ============ -->
    <aside
      v-if="!create"
      class="flex flex-col w-full border-t lg:w-96 lg:h-full lg:border-t-0 lg:border-l border-n-weak bg-n-solid-1"
    >
      <header
        class="flex items-center justify-between flex-shrink-0 gap-2 px-4 py-3 border-b border-n-weak"
      >
        <h3 class="text-sm font-semibold text-n-slate-12">
          {{ t('TASKS.COMMENTS.TITLE') }}
        </h3>
        <span
          v-if="task.blocked"
          class="flex items-center gap-1 px-2 py-0.5 text-xs font-medium border rounded-full bg-n-amber-3 text-n-amber-11 border-n-amber-6"
        >
          <span class="i-lucide-octagon-x" />
          {{ t('TASKS.BADGES.BLOCKED') }}
        </span>
      </header>

      <!-- Timeline feed -->
      <div class="flex flex-col flex-1 gap-3 px-4 py-4 overflow-y-auto">
        <template v-if="comments.length">
          <template v-for="c in comments" :key="c.id">
            <!-- Activity entry (system-generated) -->
            <div
              v-if="c.comment_type === 'activity'"
              class="flex items-start gap-2 text-xs text-n-slate-11"
            >
              <span
                class="mt-1.5 w-1.5 h-1.5 rounded-full bg-n-slate-7 shrink-0"
              />
              <div class="flex-1 min-w-0">
                <span>{{ c.content }}</span>
                <span class="ml-1.5 text-n-slate-9">
                  {{ formatDay(c.created_at) }}
                </span>
              </div>
            </div>

            <!-- Regular comment -->
            <div v-else class="flex gap-2.5">
              <Avatar :name="c.user?.name || 'System'" :size="28" />
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2 mb-0.5">
                  <span class="text-sm font-medium text-n-slate-12">
                    {{ c.user?.name || 'System' }}
                  </span>
                  <span class="text-xs text-n-slate-9">
                    {{ formatDate(c.created_at) }}
                  </span>
                </div>
                <p
                  v-dompurify-html="formatMessage(c.content, false, false)"
                  class="text-sm text-n-slate-12 prose prose-sm max-w-none [&_.prosemirror-mention-node]:font-semibold"
                />
              </div>
            </div>
          </template>
        </template>
        <p v-else class="mt-4 text-sm text-center text-n-slate-10">
          {{ t('TASKS.COMMENTS.EMPTY') }}
        </p>
      </div>

      <!-- Comment composer (sticky bottom) -->
      <div class="flex-shrink-0 p-3 border-t border-n-weak">
        <div class="p-2 border rounded-lg border-n-weak bg-n-solid-2">
          <WootMessageEditor
            v-model="newComment"
            :editor-id="commentEditorId"
            :placeholder="t('TASKS.COMMENTS.PLACEHOLDER')"
            is-private
            :enable-canned-responses="false"
            :enable-variables="false"
            :focus-on-mount="false"
            class="task-comment-editor min-h-[52px] [&_.ProseMirror]:max-h-[180px] [&_.ProseMirror]:overflow-y-auto"
          />
          <div class="flex justify-end mt-2">
            <Button
              icon="i-lucide-send-horizontal"
              size="sm"
              color="blue"
              :label="t('TASKS.ACTIONS.ADD_COMMENT')"
              :disabled="!newComment.trim()"
              @click="submitComment"
            />
          </div>
        </div>
      </div>
    </aside>
  </div>
</template>
